import { useState } from 'react';

// material-ui
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// project imports
import { useUserContext, refreshUserContext } from 'services/userContextStore';
import { switchUserOrganization, switchUserRole } from 'services/api/userContext';
import { formatRequestError } from 'pages/ad-tools/shared';

// ==============================|| HEADER CONTENT - 组织/角色切换(参考 vben) ||============================== //

/**
 * vben 风格：右上角两个可搜索下拉（组织 + 角色），置于合作方名称左侧。
 * 组织下拉按 parent_id 展开成树（缩进展示，可搜索）；角色下拉为角色列表。
 * 切换成功后刷新缓存并整页刷新，保证各页面按新上下文重新拉取数据。
 */

// 参考 vben buildOrganizationLevelOptions：把组织树展开成带 level 的平铺列表
function flattenOrgTree(list = []) {
  const idMap = new Map();
  const childrenMap = new Map();

  list.forEach((item) => {
    const id = String(item.id ?? '');
    if (id) idMap.set(id, item);
  });
  list.forEach((item) => {
    const parentId = String(item.parent_id ?? '');
    if (!childrenMap.has(parentId)) childrenMap.set(parentId, []);
    childrenMap.get(parentId).push(item);
  });

  const result = [];
  const visited = new Set();
  const pushNode = (item, level) => {
    const key = String(item.id ?? `${item.name}_${result.length}`);
    if (visited.has(key)) return;
    visited.add(key);
    result.push({ ...item, level });
    childrenMap.get(key)?.forEach((child) => pushNode(child, level + 1));
  };

  const roots = list.filter((item) => {
    const id = String(item.id ?? '');
    const parentId = String(item.parent_id ?? '');
    return !parentId || parentId === id || !idMap.has(parentId);
  });
  roots.forEach((item) => pushNode(item, 0));
  list.forEach((item) => pushNode(item, 0));

  return result;
}

function keyOf(value) {
  return value === undefined || value === null || value === '' ? '' : String(value);
}

function selectSx(width) {
  return {
    width,
    flexShrink: 0,
    '& .MuiOutlinedInput-root': { height: 30, fontSize: 12 },
    '& .MuiAutocomplete-input': { py: '3px !important', fontSize: 12 },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' }
  };
}

export default function OrgRoleSwitcher() {
  const context = useUserContext();
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState('');

  if (!context) return null;

  const orgs = Array.isArray(context.switchable_organizations) ? context.switchable_organizations : [];
  const roles = Array.isArray(context.available_roles) ? context.available_roles : [];
  const currentOrg = context.current_organization || null;
  const currentRole = context.active_role || null;

  const orgOptions = flattenOrgTree(orgs);
  const roleOptions = roles.map((role) => ({
    ...role,
    name: role.role_name || role.name || `角色 #${role.id}`
  }));

  // 与 vben 一致：current_organization 为空时（如纯 GLOBAL 用户无成员组织）默认展示第一个组织，便于下拉有默认选中；
  // 仅为展示层 fallback，不修改实际上下文，切换时才真正激活组织。
  const currentOrgOption = orgOptions.find((option) => keyOf(option.id) === keyOf(currentOrg?.id)) || orgOptions[0] || null;
  // active_role 为 null（组织下多个角色均未激活时）默认展示第一个角色，便于下拉有默认选中；
  // 仅为展示层 fallback，不修改实际上下文，切换时才真正激活角色。
  const currentRoleOption = roleOptions.find((option) => keyOf(option.id) === keyOf(currentRole?.id)) || roleOptions[0] || null;
  const busy = switching;

  const showError = (err) => setError(formatRequestError(err));

  const doSwitch = async (switchFn) => {
    setSwitching(true);
    setError('');
    try {
      await switchFn();
      try {
        await refreshUserContext();
      } catch {
        // 上下文已切换成功，刷新失败不阻塞整页刷新
      }
      window.location.reload();
    } catch (err) {
      showError(err);
      setSwitching(false);
    }
  };

  const handleSwitchOrg = (option) => {
    if (!option || keyOf(option.id) === keyOf(currentOrg?.id)) return;
    doSwitch(() => switchUserOrganization({ organization_id: option.id }));
  };

  const handleSwitchRole = (option) => {
    if (!option || keyOf(option.id) === keyOf(currentRole?.id)) return;
    doSwitch(() => switchUserRole({ role_id: option.id }));
  };

  const hasOrgOptions = orgOptions.length > 0;
  const hasRoleOptions = roleOptions.length > 0;
  if (!hasOrgOptions && !hasRoleOptions) return null;

  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75, ml: 0.5, mr: 1 }}>
      {error && (
        <Tooltip title={error}>
          <Typography
            component="span"
            sx={{
              fontSize: 12,
              color: 'error.main',
              maxWidth: 180,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              cursor: 'help'
            }}
          >
            {error}
          </Typography>
        </Tooltip>
      )}

      {/* 组织切换（可搜索，树形缩进） */}
      {hasOrgOptions && (
        <Autocomplete
          size="small"
          disableClearable
          autoHighlight
          options={orgOptions}
          getOptionLabel={(option) => option?.name || ''}
          value={currentOrgOption}
          onChange={(_, option) => handleSwitchOrg(option)}
          disabled={orgs.length <= 1 || busy}
          loading={busy}
          sx={selectSx(180)}
          renderOption={(props, option) => (
            <Box component="li" {...props} style={{ paddingLeft: (2 + (option.level || 0) * 2) * 8 }}>
              <Stack sx={{ lineHeight: 1.25 }}>
                <Typography component="span" sx={{ fontSize: 13 }}>
                  {option.name || `组织 #${option.id}`}
                </Typography>
                {option.identity_text && (
                  <Typography component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>
                    {option.identity_text}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
          renderInput={(params) => <TextField {...params} placeholder="选择组织" title="切换组织" />}
        />
      )}

      {/* 角色切换（可搜索） */}
      {hasRoleOptions && (
        <Autocomplete
          size="small"
          disableClearable
          autoHighlight
          options={roleOptions}
          getOptionLabel={(option) => option?.name || ''}
          value={currentRoleOption}
          onChange={(_, option) => handleSwitchRole(option)}
          disabled={roles.length <= 1 || busy}
          loading={busy}
          sx={selectSx(140)}
          renderOption={(props, option) => (
            <Box component="li" {...props} sx={{ py: 0.5 }}>
              <Stack sx={{ lineHeight: 1.25 }}>
                <Typography component="span" sx={{ fontSize: 13 }}>
                  {option.name}
                </Typography>
                {option.data_scope_mode_text && (
                  <Typography component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>
                    {option.data_scope_mode_text}
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
          renderInput={(params) => <TextField {...params} placeholder="选择角色" title="切换角色" />}
        />
      )}
    </Stack>
  );
}
